import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  AdminCreateWorkspaceData,
  AdminTilesWorkspaceData,
  AdminUsersResponse,
} from "@/app/admin/adminTypes";
import { apiFetch } from "@/lib/apiClient";
import { queryKeys } from "@/lib/queryKeys";

async function readJson<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: string;
  };

  if (!response.ok) {
    throw new Error(payload.error ?? "Request failed");
  }

  return payload;
}

export async function fetchAdminTilesWorkspace(): Promise<AdminTilesWorkspaceData> {
  return readJson<AdminTilesWorkspaceData>(
    await apiFetch("/api/admin/tiles"),
  );
}

export async function fetchAdminCreateWorkspace(): Promise<AdminCreateWorkspaceData> {
  return readJson<AdminCreateWorkspaceData>(
    await apiFetch("/api/admin/create"),
  );
}

export async function fetchAdminUsers(): Promise<AdminUsersResponse> {
  return readJson<AdminUsersResponse>(
    await apiFetch("/api/admin/users"),
  );
}

export function useAdminCreateWorkspaceQuery(initialData?: AdminCreateWorkspaceData) {
  return useQuery({
    queryKey: queryKeys.admin.create.workspace(),
    queryFn: fetchAdminCreateWorkspace,
    initialData,
    staleTime: 0,
  });
}

export function useAdminTilesWorkspaceQuery(initialData?: AdminTilesWorkspaceData) {
  return useQuery({
    queryKey: queryKeys.admin.tiles.workspace(),
    queryFn: fetchAdminTilesWorkspace,
    initialData,
    staleTime: 0,
  });
}

export function useAdminUsersQuery(initialData?: AdminUsersResponse) {
  return useQuery({
    queryKey: queryKeys.admin.users.list(),
    queryFn: fetchAdminUsers,
    initialData,
    staleTime: 30_000,
  });
}

export function useUploadAdminTilesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: FormData) =>
      readJson<{ tiles?: Array<{ filename: string }> }>(
        await apiFetch("/api/admin/tiles", {
          method: "POST",
          body: formData,
          headers: {},
        }),
      ),
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.tiles.workspace() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.tileCatalog.payload() });
    },
  });
}
